// src/services/companionModuleContext.js
//
// Marlow is told which module the person was reading, so she starts from
// there rather than from nothing. Three small pieces live here:
//
//   - what is being read right now, held in memory only by the module page
//     and its reader, so the menu link into Marlow can carry it;
//   - the module a conversation was opened from, kept in sessionStorage so it
//     survives the sign-in redirect (cleared by "Talk about something else", a
//     new conversation, deleting what Marlow keeps, or the device data
//     delete on the privacy page);
//   - the userContext fields the Knowledge Hub renders as module context.
//
// The Hub accepts currentModule, moduleName and sectionName. Without
// currentModule the other two render nothing, so they are never sent alone.
// Values are short titles, never module content.

export const MODULE_KEY = 'aos_marlow_module_v1';
const MAX_AGE_MS = 12 * 60 * 60 * 1000;
const COMPANION_PATH = '/marlow';

const tidy = (m) => (m && m.id != null && m.id !== '' && m.title
  ? { id: String(m.id), title: String(m.title), section: m.section ? String(m.section) : null }
  : null);

let reading = null;

export const setReadingModule = (m) => { reading = tidy(m); };

export const setReadingSection = (id, section) => {
  if (reading && reading.id === String(id)) reading = { ...reading, section: section || null };
};

export const clearReadingModule = (id) => {
  if (reading && (id == null || reading.id === String(id))) reading = null;
};

export const getReadingModule = () => reading;

export const saveModuleContext = (m) => {
  const ctx = tidy(m);
  try {
    if (ctx) sessionStorage.setItem(MODULE_KEY, JSON.stringify({ ...ctx, t: Date.now() }));
    else sessionStorage.removeItem(MODULE_KEY);
  } catch { /* private mode: the context still rides this visit's messages */ }
  return ctx;
};

export const loadModuleContext = () => {
  try {
    const raw = sessionStorage.getItem(MODULE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    const ctx = tidy(saved);
    if (!ctx || Date.now() - (saved.t || 0) > MAX_AGE_MS) {
      sessionStorage.removeItem(MODULE_KEY);
      return null;
    }
    return ctx;
  } catch {
    return null;
  }
};

export const clearModuleContext = () => {
  try { sessionStorage.removeItem(MODULE_KEY); } catch { /* nothing to do */ }
};

// Called by a link into Marlow from elsewhere in the app: note the module
// being read, or that none is, so her page opens on the right one. A click on
// her own page leaves the conversation's module alone.
export const noteCompanionEntry = () => {
  if (typeof window !== 'undefined' && window.location.pathname.startsWith(COMPANION_PATH)) return;
  saveModuleContext(reading);
};

export const moduleUserContext = (m) => {
  const ctx = tidy(m);
  if (!ctx) return {};
  return {
    currentModule: ctx.id.slice(0, 200),
    moduleName: ctx.title.slice(0, 200),
    ...(ctx.section ? { sectionName: ctx.section.slice(0, 200) } : {}),
  };
};
